import { z } from "zod";

/**
 * Domain contracts shared by web and api. These are the wire shapes; the database
 * schema in @auradata/db may differ (secrets, internal columns).
 */

/** Warehouse kinds a connection can point at. Declared early: several read models use it. */
export const ConnectionType = z.enum([
  "Snowflake",
  "BigQuery",
  "Databricks",
  "Redshift",
  "Postgres",
  "DuckDB",
]);
export type ConnectionType = z.infer<typeof ConnectionType>;

export const RunStatus = z.enum([
  "queued",
  "running",
  "succeeded",
  "warning",
  "failed",
  "cancelled",
]);
export type RunStatus = z.infer<typeof RunStatus>;

export const WorkspaceRole = z.enum(["owner", "admin", "member", "service"]);
export type WorkspaceRole = z.infer<typeof WorkspaceRole>;

export const ProjectRole = z.enum(["admin", "developer", "viewer"]);
export type ProjectRole = z.infer<typeof ProjectRole>;

export const Workspace = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  region: z.string(),
  defaultDbtVersion: z.string(),
});
export type Workspace = z.infer<typeof Workspace>;

export const Project = z.object({
  id: z.string(),
  workspaceId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().default(""),
  repository: z.object({
    provider: z.literal("github"),
    fullName: z.string(),
    branch: z.string(),
    subdirectory: z.string(),
  }),
  ownerTeam: z.string(),
  paused: z.boolean().default(false),
  archived: z.boolean().default(false),
});
export type Project = z.infer<typeof Project>;

/** A connection is a workspace-level credential reference. Secrets never appear here. */
export const Connection = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  type: z.enum(["snowflake", "bigquery", "databricks", "redshift", "postgres"]),
  account: z.string(),
  authMethod: z.enum(["keypair", "password", "oauth", "service_account"]),
  keyExpiresAt: z.string().datetime().nullable(),
  ownerTeam: z.string(),
});
export type Connection = z.infer<typeof Connection>;

export const Environment = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  kind: z.enum(["production", "staging", "ci", "custom"]),
  connectionId: z.string(),
  schema: z.string(),
  threads: z.number().int().min(1).max(64),
});
export type Environment = z.infer<typeof Environment>;

export const Job = z.object({
  id: z.string(),
  projectId: z.string(),
  environmentId: z.string(),
  name: z.string(),
  description: z.string().default(""),
  commands: z.array(z.string().min(1)).min(1),
  trigger: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("schedule"),
      cron: z.string(),
      timezone: z.string().default("UTC"),
    }),
    z.object({ kind: z.literal("pull_request") }),
    z.object({ kind: z.literal("manual") }),
  ]),
  enabled: z.boolean().default(true),
  notifyChannelIds: z.array(z.string()).default([]),
});
export type Job = z.infer<typeof Job>;

export const Run = z.object({
  id: z.string(),
  number: z.number().int(),
  projectId: z.string(),
  jobId: z.string().nullable(),
  environmentId: z.string(),
  status: RunStatus,
  trigger: z.enum(["schedule", "manual", "pull_request", "api"]),
  triggeredBy: z.string().nullable(),
  commands: z.array(z.string()),
  overridden: z.boolean().default(false),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  durationMs: z.number().int().nullable(),
  gitSha: z.string().nullable(),
  summary: z.string().default(""),
});
export type Run = z.infer<typeof Run>;

export const RunListQuery = z.object({
  projectId: z.string(),
  environmentId: z.string().optional(),
  status: RunStatus.optional(),
  jobId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(25),
});
export type RunListQuery = z.infer<typeof RunListQuery>;

export const Paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    total: z.number().int().optional(),
  });

// ---------------------------------------------------------------------------
// Read models for the web app. Aggregates the UI needs, served by the API.
// ---------------------------------------------------------------------------

export const Me = z.object({
  user: z.object({ id: z.string(), name: z.string(), email: z.string(), initials: z.string() }),
  workspaces: z.array(
    z.object({ id: z.string(), slug: z.string(), name: z.string(), role: WorkspaceRole }),
  ),
});
export type Me = z.infer<typeof Me>;

// ---------------------------------------------------------------------------
// Onboarding — the front door: sign-in, creating a workspace, accepting an invitation.
// ---------------------------------------------------------------------------

/** Who may sign in to a workspace. `domain` admits anyone with the owner's email domain. */
export const JoinPolicy = z.enum(["domain", "invite"]);
export type JoinPolicy = z.infer<typeof JoinPolicy>;

/**
 * Free-mail providers. "Anyone at <domain>" on one of these would admit strangers, so neither
 * workspace creation nor the sign-in domain join accepts them.
 */
const PUBLIC_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "gmx.de",
  "mail.com",
  "yandex.com",
  "yandex.ru",
  "zoho.com",
  "qq.com",
  "163.com",
  "126.com",
  "hey.com",
  "fastmail.com",
  "tutanota.com",
]);
export const isPublicEmailDomain = (domain: string) =>
  PUBLIC_EMAIL_DOMAINS.has(domain.trim().toLowerCase());

/** One invitation waiting for the signed-in email, as the create-workspace screen lists it. */
export const PendingInvite = z.object({
  token: z.string(),
  workspaceName: z.string(),
  workspaceInitials: z.string(),
  inviterName: z.string(),
  role: WorkspaceRole,
  /** "2 projects", or "" when the invitation grants none. */
  projects: z.string(),
  /** "expires in 6 days" / "expired". */
  expires: z.string(),
});
export type PendingInvite = z.infer<typeof PendingInvite>;

export const InviteState = z.enum(["ok", "mismatch", "expired", "accepted", "declined"]);
export type InviteState = z.infer<typeof InviteState>;

/**
 * The invitation screen's whole read model, resolved against the signed-in user: the API
 * decides the state so the screen never re-derives it from dates or emails.
 */
export const InviteDetail = z.object({
  token: z.string(),
  state: InviteState,
  workspace: z.object({ name: z.string(), slug: z.string() }),
  inviter: z.object({ name: z.string(), email: z.string(), initials: z.string() }),
  /** "sent Tuesday". */
  sent: z.string(),
  /** "September 8" — the day it expires or expired. */
  expiresOn: z.string(),
  role: WorkspaceRole,
  /** The role in plain words: "run and edit jobs, no settings or access changes". */
  roleDescription: z.string(),
  projects: z.array(z.object({ slug: z.string(), role: ProjectRole })),
  /** The address the invitation was sent to. */
  email: z.string(),
  /** The address the caller is signed in as. */
  signedInAs: z.string(),
});
export type InviteDetail = z.infer<typeof InviteDetail>;

/** Where accepting an invitation lands: the first granted project, else the workspace. */
export const InviteAccepted = z.object({
  workspaceSlug: z.string(),
  projectSlug: z.string().nullable(),
});
export type InviteAccepted = z.infer<typeof InviteAccepted>;

export const SlugAvailability = z.object({
  slug: z.string(),
  available: z.boolean(),
  /** Empty when available; otherwise why not, in words the form can show. */
  reason: z.string(),
});
export type SlugAvailability = z.infer<typeof SlugAvailability>;

export const WorkspaceCreateInput = z.object({
  name: z.string().trim().min(1).max(60),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(40)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Lower-case letters, numbers and dashes"),
  /** IANA zone; the browser's zone is the default the form offers. */
  timezone: z.string().min(1),
  joinPolicy: JoinPolicy,
});
export type WorkspaceCreateInput = z.infer<typeof WorkspaceCreateInput>;

export const WorkspaceCreated = z.object({ slug: z.string(), name: z.string() });
export type WorkspaceCreated = z.infer<typeof WorkspaceCreated>;

export const ProjectHealth = z.enum([
  "healthy",
  "failing",
  "warning",
  "running",
  "paused",
  "archived",
]);
export type ProjectHealth = z.infer<typeof ProjectHealth>;

export const ProjectSummary = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  health: ProjectHealth,
  lastRun: z.object({ status: RunStatus, when: z.string(), job: z.string() }).nullable(),
  openIssues: z.number().int(),
  ownerTeam: z.string(),
});
export type ProjectSummary = z.infer<typeof ProjectSummary>;

/** Tiny, cheap read model for the four project-sidebar badges — no polling. */
export const ProjectBadges = z.object({
  runFailed: z.boolean(),
  testsFailed: z.number().int(),
  staleSources: z.boolean(),
  openIssues: z.number().int(),
});
export type ProjectBadges = z.infer<typeof ProjectBadges>;

export const EnvironmentSummary = z.object({
  id: z.string(),
  slug: z.enum(["prod", "staging", "ci"]).or(z.string()),
  name: z.string(),
  kind: z.enum(["production", "staging", "ci", "custom"]),
  connection: z.string(),
  schema: z.string(),
  lastRunAt: z.string().nullable(),
});
export type EnvironmentSummary = z.infer<typeof EnvironmentSummary>;

export const ProjectDetail = z.object({
  project: Project,
  environments: z.array(EnvironmentSummary),
  workspace: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
  dbtVersion: z.string(),
  syncedAgo: z.string(),
});
export type ProjectDetail = z.infer<typeof ProjectDetail>;

export const AttentionItem = z.object({
  id: z.string(),
  kind: z.enum(["tests", "source", "credential", "run"]),
  severity: z.enum(["error", "warning"]),
  title: z.string(),
  body: z.string(),
  primary: z.object({ label: z.string(), to: z.string() }),
  secondary: z
    .object({ label: z.string(), action: z.enum(["snooze", "remind", "logs"]) })
    .nullable(),
});
export type AttentionItem = z.infer<typeof AttentionItem>;

export const RunRow = z.object({
  id: z.string(),
  number: z.number().int(),
  job: z.string(),
  trigger: z.string(),
  status: RunStatus,
  result: z.string(),
  duration: z.string().nullable(),
  when: z.string(),
  overridden: z.boolean(),
  kind: z.enum(["scheduled", "manual", "ci"]),
});
export type RunRow = z.infer<typeof RunRow>;

/**
 * Home read model: what needs a decision now. Recent runs come from `GET /runs?pageSize=6`
 * (shared cache with the Runs screen); counts that carry no decision are not sent at all.
 */
export const HomeSummary = z.object({
  attention: z.array(AttentionItem),
  lastRun: RunRow.nullable(),
  failingTests: z.array(z.object({ name: z.string(), model: z.string(), rows: z.number().int() })),
  /** The run the current failures date from; one owner for a fact every row shared. */
  failingSince: z.object({ label: z.string(), runId: z.string() }).nullable(),
  scheduled: z.array(z.object({ id: z.string(), name: z.string(), next: z.string() })),
  schedulerPaused: z.boolean(),
  mainJob: z.object({ id: z.string(), name: z.string() }),
});
export type HomeSummary = z.infer<typeof HomeSummary>;

export const Notification = z.object({
  id: z.string(),
  severity: z.enum(["error", "warning", "info"]),
  text: z.string(),
  rule: z.string(),
  channel: z.string(),
  when: z.string(),
  /** Relative route within the project, e.g. `runs/run_42`. */
  to: z.string(),
  /** Project the notification belongs to, so any shell can route to it. */
  projectSlug: z.string(),
  read: z.boolean(),
});
export type Notification = z.infer<typeof Notification>;

export const SearchIndex = z.object({
  jobs: z.array(z.object({ id: z.string(), name: z.string(), meta: z.string() })),
  runs: z.array(z.object({ id: z.string(), label: z.string(), meta: z.string() })),
  tests: z.array(z.object({ id: z.string(), name: z.string(), model: z.string() })),
  sources: z.array(z.object({ id: z.string(), name: z.string(), loader: z.string() })),
  models: z.array(z.object({ id: z.string(), name: z.string(), path: z.string() })),
});
export type SearchIndex = z.infer<typeof SearchIndex>;

export const RunTriggerRequest = z.object({
  jobId: z.string(),
  environmentSlug: z.string(),
  commands: z.array(z.string()).optional(),
});
export type RunTriggerRequest = z.infer<typeof RunTriggerRequest>;

// ---------------------------------------------------------------------------
// Runs list + run detail
// ---------------------------------------------------------------------------

export const Actor = z.object({ name: z.string(), initials: z.string().nullable() });
export type Actor = z.infer<typeof Actor>;

export const RunListItem = RunRow.extend({
  triggeredBy: Actor,
  /** Present while running: percent complete and a caption like "dbt run · 87/142 models". */
  progress: z.object({ pct: z.number(), label: z.string() }).nullable(),
  /** Name of the step that failed, from `run_steps`; null unless the run failed there. */
  failedStep: z.string().nullable(),
  environmentSlug: z.string(),
});
export type RunListItem = z.infer<typeof RunListItem>;

export const RunsQuery = z.object({
  env: z.string().default("prod"),
  status: z.enum(["all", "failed", "running", "succeeded", "warning", "cancelled"]).default("all"),
  job: z.string().default("all"),
  trigger: z.enum(["all", "scheduled", "manual", "ci"]).default("all"),
  range: z.enum(["24h", "7d", "30d", "90d"]).default("7d"),
  q: z.string().default(""),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(5).max(100).default(12),
});
export type RunsQuery = z.infer<typeof RunsQuery>;

export const RunsPage = z.object({
  items: z.array(RunListItem),
  total: z.number().int(),
  totalAll: z.number().int(),
  running: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Job names from the `jobs` table for the Job filter. */
  jobs: z.array(z.string()),
  /** The job the "Run now" button starts in this environment; null when the env has no job. */
  mainJob: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** One decision tile: how often runs in the selected range finish clean. */
  stats: z.object({
    finished: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    warned: z.number().int(),
    successRate: z.number(),
  }),
  /** Buckets over the selected range, following the same filters as the table. */
  perDay: z.array(
    z.object({ day: z.string(), ok: z.number(), warn: z.number(), fail: z.number() }),
  ),
});
export type RunsPage = z.infer<typeof RunsPage>;

/** Poll target for Home, Runs and Jobs: only the runs that are still moving. */
export const ActiveRuns = z.object({
  running: z.number().int(),
  runs: z.array(
    z.object({
      id: z.string(),
      number: z.number().int(),
      status: RunStatus,
      /** Job slug, so a screen can tell which job is busy. */
      jobId: z.string(),
      progress: z.object({ pct: z.number(), label: z.string() }).nullable(),
    }),
  ),
});
export type ActiveRuns = z.infer<typeof ActiveRuns>;

export const StepState = z.enum(["pass", "fail", "run", "skip", "pending", "cancel"]);
export type StepState = z.infer<typeof StepState>;

export const RunStep = z.object({
  index: z.number().int(),
  name: z.string(),
  duration: z.string().nullable(),
  seconds: z.number(),
  sub: z.string(),
  state: StepState,
  progress: z.number().nullable(),
});
export type RunStep = z.infer<typeof RunStep>;

export const LogLevel = z.enum(["INFO", "WARN", "ERROR"]);
export const LogLine = z.object({
  n: z.number().int(),
  t: z.string(),
  level: LogLevel,
  msg: z.string(),
  hl: z.boolean().optional(),
});
export type LogLine = z.infer<typeof LogLine>;

/** Either a real line or a collapsed run of routine lines the client can expand. */
export const LogEntry = z.union([
  LogLine,
  z.object({ collapsed: z.number().int(), lines: z.array(LogLine) }),
]);
export type LogEntry = z.infer<typeof LogEntry>;

/**
 * A slice of one step's log. `from` is the cursor the client sent (0 = the whole step);
 * `nextSince` is the cursor to send next, so every line transfers exactly once.
 */
export const StepLogs = z.object({
  runId: z.string(),
  step: z.number().int(),
  from: z.number().int(),
  nextSince: z.number().int(),
  entries: z.array(LogEntry),
  streaming: z.boolean(),
});
export type StepLogs = z.infer<typeof StepLogs>;

export const RunModelRow = z.object({
  name: z.string(),
  path: z.string(),
  materialized: z.enum(["view", "table", "incremental", "ephemeral", "seed", "snapshot"]),
  status: z.enum(["success", "error", "skipped", "running"]),
  duration: z.string(),
  seconds: z.number(),
  rows: z.string().nullable(),
  changed: z.boolean(),
});
export type RunModelRow = z.infer<typeof RunModelRow>;

export const TestResultKind = z.enum(["fail", "warn", "pass", "skip"]);
export const RunTestRow = z.object({
  name: z.string(),
  model: z.string(),
  kind: TestResultKind,
  result: z.string(),
  time: z.string(),
  rows: z.number().int(),
  file: z.string(),
});
export type RunTestRow = z.infer<typeof RunTestRow>;

export const RunSourceRow = z.object({
  name: z.string(),
  loader: z.string(),
  state: z.enum(["fresh", "stale", "error"]),
  lastLoaded: z.string(),
  threshold: z.string(),
});
export type RunSourceRow = z.infer<typeof RunSourceRow>;

export const RunArtifact = z.object({
  name: z.string(),
  description: z.string(),
  size: z.string().nullable(),
  available: z.boolean(),
  /** Download href when the artifact is stored; absent for fixture rows. */
  url: z.string().optional(),
});
export type RunArtifact = z.infer<typeof RunArtifact>;

/**
 * Header, banner, steps and config — what a Logs-only visit needs. Models, tests, sources and
 * artifacts load per tab from `GET /runs/:id/{models,tests,sources,artifacts}`.
 */
export const RunDetail = z.object({
  run: RunListItem,
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  wallDuration: z.string().nullable(),
  environment: EnvironmentSummary,
  commit: z.object({ sha: z.string(), message: z.string() }),
  retries: z.number().int(),
  fixedOnRetry: z.boolean(),
  banner: z.object({
    tone: z.enum(["err", "warn", "ok", "neutral", "accent"]),
    title: z.string(),
    body: z.string(),
  }),
  steps: z.array(RunStep),
  config: z.object({
    job: z.string(),
    jobId: z.string(),
    schedule: z.string(),
    target: z.string(),
    warehouse: z.string(),
    dbt: z.string(),
    command: z.string(),
  }),
  /** Tab counts, from `count(*)` on `run_results` / `artifacts`. */
  counts: z.object({
    models: z.number().int(),
    tests: z.number().int(),
    testsFailed: z.number().int(),
    sources: z.number().int(),
    artifacts: z.number().int(),
  }),
});
export type RunDetail = z.infer<typeof RunDetail>;

/** Poll target while a run is in flight (< 500 B); a terminal status triggers one full refetch. */
export const RunStatusUpdate = z.object({
  status: RunStatus,
  progress: z.object({ pct: z.number(), label: z.string() }).nullable(),
  steps: z.array(
    z.object({
      index: z.number().int(),
      state: StepState,
      progress: z.number().nullable(),
      sub: z.string(),
    }),
  ),
});
export type RunStatusUpdate = z.infer<typeof RunStatusUpdate>;

export const RunModelsTab = z.object({
  rows: z.array(RunModelRow),
  succeeded: z.number().int(),
  errors: z.number().int(),
  skipped: z.number().int(),
});
export type RunModelsTab = z.infer<typeof RunModelsTab>;

export const RunTestsTab = z.object({
  rows: z.array(RunTestRow),
  counts: z.object({
    failed: z.number().int(),
    warned: z.number().int(),
    passed: z.number().int(),
    skipped: z.number().int(),
  }),
});
export type RunTestsTab = z.infer<typeof RunTestsTab>;

export const RunSourcesTab = z.object({ rows: z.array(RunSourceRow) });
export type RunSourcesTab = z.infer<typeof RunSourcesTab>;

export const RunArtifactsTab = z.object({ rows: z.array(RunArtifact) });
export type RunArtifactsTab = z.infer<typeof RunArtifactsTab>;

export const RunAction = z.object({
  action: z.enum(["rerun_all", "rerun_from_failure", "rerun_failed_tests", "cancel"]),
});
export type RunAction = z.infer<typeof RunAction>;

// Shared detail views for tests and models (used by Run detail, Tests, Issues)
export const FailingRows = z.object({
  test: z.string(),
  query: z.string(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string().nullable())),
  total: z.number().int(),
});
export type FailingRows = z.infer<typeof FailingRows>;

export const CompiledSql = z.object({ test: z.string(), path: z.string(), sql: z.string() });
export type CompiledSql = z.infer<typeof CompiledSql>;

// ---------------------------------------------------------------------------
// Lineage and Model detail
//
// Both screens are built from the `manifest.json` of the run that last built the project,
// plus `run_results` for what each node did. Nothing here is derived from a fixture: a node
// the project does not have cannot appear, and a number nobody measured is null.
// ---------------------------------------------------------------------------

export const NodeKind = z.enum(["model", "seed", "snapshot", "source"]);
export type NodeKind = z.infer<typeof NodeKind>;

/** How the node's last execution in the scoped environment went. */
export const NodeStatus = z.enum(["ok", "warn", "fail", "skipped", "unknown"]);
export type NodeStatus = z.infer<typeof NodeStatus>;

export const NodeTests = z.object({
  passed: z.number().int(),
  warned: z.number().int(),
  failed: z.number().int(),
});
export type NodeTests = z.infer<typeof NodeTests>;

/** One box in the graph. The layout is the client's job; the API sends facts, not geometry. */
export const LineageNode = z.object({
  /** URL id and display name — dbt node names are unique within a project. */
  id: z.string(),
  uniqueId: z.string(),
  kind: NodeKind,
  materialization: z.string(),
  folder: z.string(),
  status: NodeStatus,
  rows: z.number().int().nullable(),
  tests: NodeTests,
  hasTests: z.boolean(),
});
export type LineageNode = z.infer<typeof LineageNode>;

export const LineageGraph = z.object({
  nodes: z.array(LineageNode),
  edges: z.array(z.object({ from: z.string(), to: z.string() })),
  counts: z.object({
    nodes: z.number().int(),
    models: z.number().int(),
    seeds: z.number().int(),
    snapshots: z.number().int(),
    sources: z.number().int(),
  }),
  /** The run whose results coloured the nodes, in the scoped environment. */
  lastRun: z.object({ id: z.string(), number: z.number().int(), when: z.string() }).nullable(),
  environment: z.string(),
});
export type LineageGraph = z.infer<typeof LineageGraph>;

export const ModelNeighbour = z.object({
  id: z.string(),
  direction: z.enum(["upstream", "downstream"]),
  status: NodeStatus,
});
export type ModelNeighbour = z.infer<typeof ModelNeighbour>;

/** Header, decision strip and facts rail — the first request Model detail makes. */
export const ModelDetail = z.object({
  id: z.string(),
  uniqueId: z.string(),
  kind: NodeKind,
  materialization: z.string(),
  folder: z.string(),
  path: z.string(),
  schema: z.string(),
  description: z.string(),
  owner: z.string(),
  tags: z.array(z.string()),
  /** `+orders` — the selector the Run button sends. */
  selector: z.string(),
  /** Repository URL for `path`, when the project has a repository cached. */
  github: z.string().nullable(),
  /** The one thing to know, lead with it. */
  decision: z.object({
    tone: z.enum(["ok", "warn", "err", "neutral"]),
    title: z.string(),
    body: z.string(),
    run: z.object({ id: z.string(), number: z.number().int() }).nullable(),
    /** The first warning or failure on this node, if any. */
    failing: z.object({ test: z.string(), rows: z.number().int(), model: z.string() }).nullable(),
  }),
  facts: z.array(z.object({ key: z.string(), value: z.string(), mono: z.boolean() })),
  neighbours: z.array(ModelNeighbour),
  /** Oldest → newest outcome of the last 14 builds in this environment, by run. */
  history: z.array(z.object({ number: z.number().int(), status: NodeStatus })),
  historyNote: z.string(),
  counts: z.object({ columns: z.number().int(), runs: z.number().int() }),
  environment: z.string(),
});
export type ModelDetail = z.infer<typeof ModelDetail>;

export const ModelColumns = z.object({
  columns: z.array(
    z.object({
      name: z.string(),
      type: z.string(),
      description: z.string(),
      tests: z.array(z.object({ label: z.string(), tone: z.enum(["ok", "warn", "err"]) })),
    }),
  ),
  untested: z.number().int(),
  /** Tests worth adding, from `models.suggested_tests`. */
  suggested: z.array(z.string()),
});
export type ModelColumns = z.infer<typeof ModelColumns>;

export const ModelRuns = z.object({
  rows: z.array(
    z.object({
      runId: z.string(),
      number: z.number().int(),
      job: z.string(),
      status: NodeStatus,
      result: z.string(),
      durationMs: z.number().int(),
      rows: z.number().int().nullable(),
      when: z.string(),
    }),
  ),
  page: z.number().int(),
  pages: z.number().int(),
  total: z.number().int(),
});
export type ModelRuns = z.infer<typeof ModelRuns>;

export const ModelSql = z.object({
  path: z.string(),
  source: z.string(),
  compiled: z.string(),
  /** The run whose manifest these came from. */
  run: z.object({ id: z.string(), number: z.number().int() }).nullable(),
});
export type ModelSql = z.infer<typeof ModelSql>;

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export const Channel = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["slack", "pagerduty", "email", "github", "webhook", "teams"]),
  target: z.string(),
});
export type Channel = z.infer<typeof Channel>;

export const JobTrigger = z.enum(["hourly", "daily", "weekly", "cron", "pr"]);
export type JobTrigger = z.infer<typeof JobTrigger>;

/** One row of the Jobs table. The drawer and the form load `JobDetail` from `GET /jobs/:jobId`. */
export const JobSummary = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  environmentName: z.string(),
  /** Warehouse the environment writes to; the row shows its mark, not the connection name. */
  connectionType: ConnectionType,
  kind: z.enum(["schedule", "ci"]),
  trigger: JobTrigger,
  cron: z.string().nullable(),
  scheduleLabel: z.string(),
  enabled: z.boolean(),
  next: z.string(),
  lastRun: z
    .object({
      id: z.string(),
      number: z.number().int(),
      status: RunStatus,
      result: z.string(),
      when: z.string(),
      duration: z.string(),
      overridden: z.boolean(),
    })
    .nullable(),
  running: z.boolean(),
});
export type JobSummary = z.infer<typeof JobSummary>;

/** Everything the drawer and the edit form need on top of the row. */
export const JobDetail = JobSummary.extend({
  environmentSlug: z.string(),
  connectionName: z.string(),
  schema: z.string(),
  commands: z.array(z.string()),
  threads: z.number().int(),
  timeout: z.string(),
  target: z.string(),
  notifyChannelIds: z.array(z.string()),
  notifyLabel: z.string(),
  generateDocs: z.boolean(),
  defer: z.boolean(),
});
export type JobDetail = z.infer<typeof JobDetail>;

export const JobsPage = z.object({
  jobs: z.array(JobSummary),
  schedulerPaused: z.boolean(),
});
export type JobsPage = z.infer<typeof JobsPage>;

export const JobInput = z.object({
  name: z.string().min(1),
  description: z.string(),
  commands: z.array(z.string().min(1)).min(1),
  trigger: JobTrigger,
  cron: z.string().nullable(),
  environmentSlug: z.string(),
  notifyChannelIds: z.array(z.string()),
  generateDocs: z.boolean(),
  defer: z.boolean(),
  threads: z.number().int().min(1).max(32),
  timeout: z.string(),
});
export type JobInput = z.infer<typeof JobInput>;

export const JobAction = z.object({
  action: z.enum(["pause", "resume", "run", "delete"]),
  commands: z.array(z.string()).optional(),
  saveCommands: z.boolean().optional(),
});
export type JobAction = z.infer<typeof JobAction>;

// ---------------------------------------------------------------------------
// Tests screen
// ---------------------------------------------------------------------------

export const TestType = z.enum([
  "not_null",
  "unique",
  "relationships",
  "accepted_values",
  "custom",
]);
export type TestType = z.infer<typeof TestType>;
export const TestSummary = z.object({
  name: z.string(),
  model: z.string(),
  type: TestType,
  status: z.enum(["fail", "warn", "pass"]),
  severity: z.string(),
  rows: z.number().int(),
  lastRun: z.string(),
  /** 14 chars of g/w/f oldest → newest. */
  history: z.string(),
  muted: z.boolean(),
  flaky: z.boolean(),
});
export type TestSummary = z.infer<typeof TestSummary>;

/** Loaded when a test row is expanded: `GET /tests/:test/recent`. */
export const TestDetail = z.object({
  description: z.string(),
  file: z.string(),
  owner: z.string(),
  config: z.array(z.object({ key: z.string(), value: z.string() })),
  recent: z.array(
    z.object({
      run: z.string(),
      runId: z.string(),
      result: z.enum(["fail", "warn", "pass"]),
      when: z.string(),
    }),
  ),
});
export type TestDetail = z.infer<typeof TestDetail>;

export const UncoveredModel = z.object({
  model: z.string(),
  materialization: z.string(),
  folder: z.string(),
  downstream: z.number().int(),
  suggested: z.array(z.string()),
});
export type UncoveredModel = z.infer<typeof UncoveredModel>;

/** A run this screen queued, so the caller can navigate to it. `null` when nothing was queued. */
export const QueuedRun = z.object({ id: z.string(), number: z.number().int() }).nullable();
export type QueuedRun = z.infer<typeof QueuedRun>;
export const QualityActionResult = z.object({ ok: z.boolean(), run: QueuedRun });
export type QualityActionResult = z.infer<typeof QualityActionResult>;

export const TestsPage = z.object({
  tests: z.array(TestSummary),
  total: z.number().int(),
  modelsTotal: z.number().int(),
  covered: z.number().int(),
  lastFullRun: z.object({ id: z.string(), number: z.number().int(), when: z.string() }).nullable(),
  uncovered: z.array(UncoveredModel),
  /** False when the requested environment has no test results of its own. */
  scoped: z.boolean(),
});
export type TestsPage = z.infer<typeof TestsPage>;

export const TestsAction = z.object({
  action: z.enum(["mute", "unmute", "rerun", "run_all", "add_suggested", "add_all_suggested"]),
  test: z.string().optional(),
  model: z.string().optional(),
  env: z.string().optional(),
});
export type TestsAction = z.infer<typeof TestsAction>;

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

export const SourceSummary = z.object({
  name: z.string(),
  loader: z.string(),
  lagMinutes: z.number().int(),
  warnAfter: z.string(),
  state: z.enum(["fresh", "stale", "error"]),
  usedBy: z.number().int(),
  /** 24 chars of g/w/f oldest → newest. */
  history: z.string(),
  /** ISO timestamp while alerts are snoozed for this source; omitted when it is not snoozed. */
  snoozedUntil: z.string().nullish(),
});
export type SourceSummary = z.infer<typeof SourceSummary>;

/** Loaded when a source row is expanded: `GET /sources/:name/checks`. */
export const SourceChecks = z.object({
  note: z.string(),
  schedule: z.string(),
  rows: z.string(),
  warnAfter: z.string(),
  errorAfter: z.string(),
  file: z.string(),
  loadedAtField: z.string(),
  columns: z.number().int(),
  downstream: z.array(z.string()),
  lastLoaded: z.string(),
  checks: z.array(
    z.object({
      runId: z.string(),
      run: z.string(),
      status: z.enum(["fresh", "stale", "error"]),
      minutes: z.number().int().nullable(),
      when: z.string(),
    }),
  ),
});
export type SourceChecks = z.infer<typeof SourceChecks>;

export const SourcesPage = z.object({
  sources: z.array(SourceSummary),
  loaders: z.array(z.string()),
  /** Latest run that reported source freshness results, or `null` if none has. */
  lastCheck: z.object({ id: z.string(), number: z.number().int(), when: z.string() }).nullable(),
  /** False when the requested environment has no freshness results of its own. */
  scoped: z.boolean(),
});
export type SourcesPage = z.infer<typeof SourcesPage>;

export const SourcesAction = z.object({
  action: z.enum(["check", "check_all", "snooze", "unsnooze", "thresholds"]),
  source: z.string().optional(),
  warnAfter: z.string().optional(),
  errorAfter: z.string().optional(),
  env: z.string().optional(),
});
export type SourcesAction = z.infer<typeof SourcesAction>;

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

export const IssueKind = z.enum([
  "Test failure",
  "Compilation",
  "Freshness",
  "Warehouse",
  "Timeout",
  "Credentials",
]);
export const IssueSummary = z.object({
  id: z.string(),
  title: z.string(),
  kind: IssueKind,
  severity: z.enum(["error", "warning"]),
  status: z.enum(["open", "muted", "resolved"]),
  count: z.number().int(),
  lastSeen: z.string(),
  /** ISO timestamp of the last occurrence; the list sorts on this, not the label. */
  lastSeenAt: z.string(),
  jobs: z.array(z.string()),
  models: z.array(z.string()),
  isNew: z.boolean(),
  regressed: z.boolean(),
  example: z.string(),
  /** ISO timestamp while the issue is muted; omitted when it is not muted. */
  mutedUntil: z.string().nullish(),
  /** Id of the run the issue was last seen in; omitted when no run is attached. */
  latestRunId: z.string().nullish(),
});
export type IssueSummary = z.infer<typeof IssueSummary>;

/** Loaded when an issue row is expanded: `GET /issues/:id`. */
export const IssueDetail = z.object({
  full: z.string(),
  hint: z.string(),
  occurrences: z.array(
    z.object({ runId: z.string(), run: z.string(), job: z.string(), when: z.string() }),
  ),
  more: z.number().int(),
  models: z.array(z.string()),
});
export type IssueDetail = z.infer<typeof IssueDetail>;

export const IssuesPage = z.object({
  issues: z.array(IssueSummary),
  /** False when the requested environment has no issues of its own. */
  scoped: z.boolean(),
});
export type IssuesPage = z.infer<typeof IssuesPage>;

export const IssuesAction = z.object({
  action: z.enum(["mute", "resolve", "reopen"]),
  id: z.string(),
});
export type IssuesAction = z.infer<typeof IssuesAction>;

// ---------------------------------------------------------------------------
// Alert rules
// ---------------------------------------------------------------------------

export const AlertEvent = z.enum([
  "run_failed",
  "test_failed",
  "source_stale",
  "issue_new",
  "issue_regressed",
  "run_duration",
  "credential_expiry",
]);
export type AlertEvent = z.infer<typeof AlertEvent>;

export const AlertThrottle = z.enum([
  "immediately",
  "once per issue",
  "once per 6h",
  "once per day",
]);

export const AlertRuleInput = z.object({
  name: z.string().min(1),
  event: AlertEvent,
  severity: z.enum(["any", "error", "warning"]),
  jobs: z.array(z.string()),
  selector: z.string(),
  channelIds: z.array(z.string()).min(1),
  throttle: AlertThrottle,
  threshold: z.string(),
  enabled: z.boolean(),
});
export type AlertRuleInput = z.infer<typeof AlertRuleInput>;

export const AlertRule = AlertRuleInput.extend({ id: z.string(), lastFired: z.string() });
export type AlertRule = z.infer<typeof AlertRule>;

/** One row of the "Recent deliveries" card, straight from `alert_deliveries`. */
export const AlertDelivery = z.object({
  id: z.string(),
  severity: z.enum(["error", "warning", "info"]),
  ok: z.boolean(),
  text: z.string(),
  rule: z.string(),
  channel: z.string(),
  when: z.string(),
  /** Run the alert was raised for, when there was one. */
  runId: z.string().nullable(),
});
export type AlertDelivery = z.infer<typeof AlertDelivery>;

export const AlertsPage = z.object({
  rules: z.array(AlertRule),
  channels: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      kind: z.string(),
      rules: z.number().int(),
      /** Outcome of the last delivery attempted for this channel; `null` = never used. */
      lastDelivery: z.object({ ok: z.boolean(), when: z.string() }).nullable(),
    }),
  ),
  /** Deliveries recorded in the last 7 days, split by outcome. */
  deliveries7d: z.object({ sent: z.number().int(), failed: z.number().int() }),
  recent: z.array(AlertDelivery),
  jobs: z.array(z.string()),
});
export type AlertsPage = z.infer<typeof AlertsPage>;

export const AlertsAction = z.object({
  action: z.enum(["enable", "disable", "delete"]),
  id: z.string(),
});
export type AlertsAction = z.infer<typeof AlertsAction>;

/** Result of `POST /channels/:id/test`: a real `deliver()` call, recorded as a delivery. */
export const ChannelTestResult = z.object({ ok: z.boolean(), detail: z.string() });
export type ChannelTestResult = z.infer<typeof ChannelTestResult>;

// ---------------------------------------------------------------------------
// Workspace fixtures shared by project screens: connections, teams, members
// ---------------------------------------------------------------------------

export const ConnectionTestStatus = z.enum(["queued", "running", "ok", "fail"]);
export type ConnectionTestStatus = z.infer<typeof ConnectionTestStatus>;

export const ConnectionSummary = z.object({
  id: z.string(),
  name: z.string(),
  type: ConnectionType,
  account: z.string(),
  role: z.string(),
  auth: z.enum(["Key-pair", "Password", "OAuth", "Service account"]),
  keyExpiry: z.string().nullable(),
  keyWarning: z.boolean(),
  usedBy: z.string(),
  ownerTeam: z.string(),
  health: z.enum(["connected", "testing", "degraded", "untested"]),
  /** Outcome of the most recent `dbt debug` on this connection, or null when never tested. */
  lastTest: z
    .object({ status: ConnectionTestStatus, message: z.string(), at: z.string() })
    .nullable(),
});
export type ConnectionSummary = z.infer<typeof ConnectionSummary>;

/** Pick-list row: what a `Picker` needs and nothing more. */
export const ConnectionPick = z.object({
  id: z.string(),
  name: z.string(),
  type: ConnectionType,
  account: z.string(),
});
export type ConnectionPick = z.infer<typeof ConnectionPick>;

export const Team = z.object({
  id: z.string(),
  name: z.string(),
  meta: z.string(),
  group: z.string(),
});
export type Team = z.infer<typeof Team>;

export const WorkspaceMember = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  role: z.enum(["owner", "admin", "member", "service"]),
  identity: z.enum(["okta", "sso", "local", "token"]),
  mfa: z.boolean(),
  groups: z.array(z.string()),
  grants: z.array(z.object({ project: z.string(), role: ProjectRole })),
  seat: z.enum(["developer", "viewer", "service"]),
  lastActive: z.string(),
  deprovisioned: z.boolean(),
  you: z.boolean(),
});
export type WorkspaceMember = z.infer<typeof WorkspaceMember>;

// ---------------------------------------------------------------------------
// Environments screen
// ---------------------------------------------------------------------------

export const EnvironmentConfig = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  kind: z.enum(["production", "staging", "ci", "custom"]),
  connectionId: z.string(),
  connectionName: z.string(),
  database: z.string(),
  schema: z.string(),
  size: z.enum(["XS", "S", "M", "L"]),
  threads: z.number().int(),
  role: z.string(),
  jobs: z.number().int(),
});
export type EnvironmentConfig = z.infer<typeof EnvironmentConfig>;

export const EnvironmentsPage = z.object({
  environments: z.array(EnvironmentConfig),
  inUse: z.array(
    z.object({
      connectionId: z.string(),
      name: z.string(),
      type: ConnectionType,
      meta: z.string(),
      usedBy: z.string(),
      keyLine: z.string(),
      warning: z.boolean(),
      testing: z.boolean(),
      /** Outcome of the last `dbt debug` on this connection. */
      status: z.enum(["connected", "failed", "never"]),
      lastTest: z.string(),
    }),
  ),
  workspaceConnections: z.number().int(),
});
export type EnvironmentsPage = z.infer<typeof EnvironmentsPage>;

export const EnvironmentInput = z.object({
  name: z.string().min(1),
  kind: z.enum(["production", "staging", "ci", "custom"]),
  connectionId: z.string(),
  schema: z.string().min(1),
  size: z.enum(["XS", "S", "M", "L"]),
  threads: z.number().int(),
});
export type EnvironmentInput = z.infer<typeof EnvironmentInput>;

export const EnvironmentsAction = z.object({
  action: z.enum(["test_all", "test", "delete", "set_production"]),
  id: z.string().optional(),
  connectionId: z.string().optional(),
});
export type EnvironmentsAction = z.infer<typeof EnvironmentsAction>;

// ---------------------------------------------------------------------------
// Repository screen
// ---------------------------------------------------------------------------

export const RepositorySettings = z.object({
  branch: z.string(),
  subdirectory: z.string(),
  prCi: z.boolean(),
  autoDeploy: z.boolean(),
});
export type RepositorySettings = z.infer<typeof RepositorySettings>;

export const RepositoryPage = z.object({
  fullName: z.string(),
  url: z.string(),
  lastSynced: z.string(),
  sha: z.string(),
  settings: RepositorySettings,
});
export type RepositoryPage = z.infer<typeof RepositoryPage>;

// ---------------------------------------------------------------------------
// Project settings screen
// ---------------------------------------------------------------------------

export const ProjectSettingsGeneral = z.object({
  name: z.string().min(1),
  slug: z.string(),
  description: z.string(),
  ownerTeamId: z.string(),
  dbtVersion: z.enum(["1.8", "1.9", "1.10"]),
  timezone: z.string(),
  logRetention: z.enum(["30d", "90d", "1y"]),
  artifacts: z.boolean(),
});
export type ProjectSettingsGeneral = z.infer<typeof ProjectSettingsGeneral>;

/**
 * Base payload: enough for the nav, header and the tabs that stay cheap (Notifications,
 * Danger zone). General/Members/Tokens load their own data per tab (`GET /settings/<tab>`) so
 * opening the General tab does not also pay for the workspace's member list.
 */
export const ProjectSettingsPage = z.object({
  general: ProjectSettingsGeneral,
  notifications: z.object({ failures: z.boolean(), digest: z.boolean(), mentions: z.boolean() }),
  schedulerPaused: z.boolean(),
  jobsCount: z.number().int(),
});
export type ProjectSettingsPage = z.infer<typeof ProjectSettingsPage>;

export const ProjectSettingsMember = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  role: z.enum(["admin", "developer", "viewer", "service"]),
  lastActive: z.string(),
  auth: z.string(),
  you: z.boolean(),
});
export type ProjectSettingsMember = z.infer<typeof ProjectSettingsMember>;

export const ProjectSettingsGeneralTab = z.object({
  general: ProjectSettingsGeneral,
  envVars: z.array(z.object({ key: z.string(), value: z.string(), secret: z.boolean() })),
});
export type ProjectSettingsGeneralTab = z.infer<typeof ProjectSettingsGeneralTab>;

export const ProjectSettingsMembersTab = z.object({
  members: z.array(ProjectSettingsMember),
  /** Workspace owners/admins who can act on this project without an explicit grant. */
  adminCount: z.number().int(),
  pending: z.array(z.object({ email: z.string(), role: z.string(), when: z.string() })),
});
export type ProjectSettingsMembersTab = z.infer<typeof ProjectSettingsMembersTab>;

export const ProjectSettingsTokensTab = z.object({
  tokens: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      prefix: z.string(),
      scope: z.string(),
      lastUsed: z.string(),
      expires: z.string(),
    }),
  ),
});
export type ProjectSettingsTokensTab = z.infer<typeof ProjectSettingsTokensTab>;

export const ProjectSettingsAction = z.object({
  action: z.enum([
    "save_general",
    "add_member",
    "set_role",
    "remove_member",
    "revoke_invite",
    "set_notification",
    "create_token",
    "revoke_token",
    "pause",
    "resume",
    "delete",
  ]),
  general: ProjectSettingsGeneral.optional(),
  memberId: z.string().optional(),
  role: z.string().optional(),
  email: z.string().optional(),
  key: z.string().optional(),
  value: z.boolean().optional(),
  tokenId: z.string().optional(),
  tokenName: z.string().optional(),
  tokenScope: z.string().optional(),
  tokenExpiryDays: z.number().int().optional(),
});
export type ProjectSettingsAction = z.infer<typeof ProjectSettingsAction>;

// ---------------------------------------------------------------------------
// Workspace screens
// ---------------------------------------------------------------------------

export const AccessGrant = z.object({
  kind: z.enum(["group", "member"]),
  id: z.string(),
  name: z.string(),
  sub: z.string(),
  role: ProjectRole,
});
export type AccessGrant = z.infer<typeof AccessGrant>;

/** One row of the workspace Projects table. Grants and members live on `ProjectGovernance`. */
export const WorkspaceProject = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  health: ProjectHealth,
  lastRun: z.object({ when: z.string(), job: z.string() }).nullable(),
  openIssues: z.number().int(),
  freshness: z.object({ fresh: z.number().int(), total: z.number().int() }),
  ownerTeamId: z.string(),
  jobs: z.number().int(),
  models: z.number().int(),
  repo: z.string(),
  branch: z.string(),
  subdirectory: z.string(),
  version: z.string(),
  environments: z.array(z.object({ name: z.string(), connectionId: z.string() })),
  paused: z.boolean(),
  archived: z.boolean(),
});
export type WorkspaceProject = z.infer<typeof WorkspaceProject>;

export const WorkspaceProjectsPage = z.object({
  projects: z.array(WorkspaceProject),
  openIssues: z.number().int(),
});
export type WorkspaceProjectsPage = z.infer<typeof WorkspaceProjectsPage>;

/** Loaded by the governance drawer only (`GET /projects/:project/governance`). */
export const ProjectGovernance = z.object({
  slug: z.string(),
  name: z.string(),
  repo: z.string(),
  ownerTeamId: z.string(),
  paused: z.boolean(),
  archived: z.boolean(),
  grants: z.array(AccessGrant),
  inheritedAdmins: z.number().int(),
});
export type ProjectGovernance = z.infer<typeof ProjectGovernance>;

export const GovernanceInput = z.object({
  ownerTeamId: z.string(),
  paused: z.boolean(),
  archived: z.boolean(),
  grants: z.array(AccessGrant),
});
export type GovernanceInput = z.infer<typeof GovernanceInput>;

export const WorkspaceProjectsAction = z.object({
  action: z.enum(["pause", "resume", "delete"]),
  ids: z.array(z.string()).min(1),
});
export type WorkspaceProjectsAction = z.infer<typeof WorkspaceProjectsAction>;

/** Non-secret, per-warehouse settings. Shown back to users; stored in Postgres. */
export const ConnectionDetails = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("DuckDB"),
    /** MotherDuck database name (`md:<database>`). */
    database: z.string(),
  }),
  z.object({
    type: z.literal("Postgres"),
    host: z.string(),
    port: z.number().int().min(1).max(65535).default(5432),
    database: z.string(),
    user: z.string(),
    ssl: z.boolean().default(true),
  }),
  z.object({
    type: z.literal("Snowflake"),
    account: z.string(),
    user: z.string(),
    role: z.string().default(""),
    warehouse: z.string(),
    database: z.string(),
    authMethod: z.enum(["password", "keypair"]).default("password"),
  }),
  z.object({
    type: z.literal("BigQuery"),
    project: z.string(),
    dataset: z.string(),
    location: z.string().default("US"),
  }),
  z.object({
    type: z.literal("Databricks"),
    host: z.string(),
    httpPath: z.string(),
    catalog: z.string().default(""),
  }),
  z.object({
    type: z.literal("Redshift"),
    host: z.string(),
    port: z.number().int().min(1).max(65535).default(5439),
    database: z.string(),
    user: z.string(),
  }),
]);
export type ConnectionDetails = z.infer<typeof ConnectionDetails>;

/** Write-only credentials. Which keys apply depends on the type; blank keeps the stored value. */
export const ConnectionSecrets = z.object({
  motherduckToken: z.string().optional(),
  password: z.string().optional(),
  privateKey: z.string().optional(),
  privateKeyPassphrase: z.string().optional(),
  serviceAccountJson: z.string().optional(),
  token: z.string().optional(),
});
export type ConnectionSecrets = z.infer<typeof ConnectionSecrets>;

/** Which secret fields each type needs (UI form + API validation share this). */
export const connectionSecretFields: Record<ConnectionType, (keyof ConnectionSecrets)[]> = {
  DuckDB: ["motherduckToken"],
  Postgres: ["password"],
  Snowflake: ["password", "privateKey", "privateKeyPassphrase"],
  BigQuery: ["serviceAccountJson"],
  Databricks: ["token"],
  Redshift: ["password"],
};

export const ConnectionInput = z.object({
  name: z.string().min(1),
  details: ConnectionDetails,
  secrets: ConnectionSecrets.optional(),
  ownerTeam: z.string().default(""),
  /** Project slugs allowed to use this connection; null = every project. */
  allowedProjects: z.array(z.string()).nullable().default(null),
});
export type ConnectionInput = z.infer<typeof ConnectionInput>;

export const ConnectionUpdate = z.object({
  name: z.string().min(1).optional(),
  details: ConnectionDetails.optional(),
  secrets: ConnectionSecrets.optional(),
  ownerTeam: z.string().optional(),
  allowedProjects: z.array(z.string()).nullable().optional(),
});
export type ConnectionUpdate = z.infer<typeof ConnectionUpdate>;

export const ConnectionTest = z.object({
  id: z.string(),
  status: ConnectionTestStatus,
  message: z.string(),
  /** Relative label ("2 min ago"). */
  at: z.string(),
});
export type ConnectionTest = z.infer<typeof ConnectionTest>;

export const ConnectionDetail = ConnectionSummary.extend({
  details: ConnectionDetails,
  credentials: z.object({
    configured: z.boolean(),
    setAt: z.string().nullable(),
    setBy: z.string().nullable(),
  }),
  lastTest: ConnectionTest.nullable(),
  allowedProjects: z.array(z.string()).nullable(),
  environments: z.array(
    z.object({
      project: z.string(),
      projectSlug: z.string(),
      environment: z.string(),
      environmentSlug: z.string(),
    }),
  ),
});
export type ConnectionDetail = z.infer<typeof ConnectionDetail>;

/** Bulk test. `ids` is required and non-empty: the server never fans out over a whole workspace. */
export const ConnectionsAction = z.object({
  action: z.literal("test_all"),
  ids: z.array(z.string()).min(1),
});
export type ConnectionsAction = z.infer<typeof ConnectionsAction>;

export const WorkspaceGroup = z.object({
  id: z.string(),
  name: z.string(),
  oktaId: z.string(),
  synced: z.string(),
  people: z.number().int(),
  mappings: z.array(z.object({ project: z.string(), role: ProjectRole })),
});
export type WorkspaceGroup = z.infer<typeof WorkspaceGroup>;

export const ServiceAccount = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  owner: z.string(),
  tokens: z.number().int(),
  scopes: z.string(),
  lastUsed: z.string(),
  enabled: z.boolean(),
});
export type ServiceAccount = z.infer<typeof ServiceAccount>;

export const AccessRequest = z.object({
  id: z.string(),
  requester: z.string(),
  email: z.string(),
  team: z.string(),
  project: z.string(),
  role: ProjectRole,
  reason: z.string(),
  when: z.string(),
  external: z.boolean(),
});
export type AccessRequest = z.infer<typeof AccessRequest>;

export const RoleDefinition = z.object({
  id: z.string(),
  name: z.string(),
  custom: z.boolean(),
  scope: z.enum(["Workspace", "Project"]),
  permissions: z.array(z.string()),
  assigned: z.number().int(),
});
export type RoleDefinition = z.infer<typeof RoleDefinition>;

/**
 * Header and tab counts only. Each tab loads its own rows:
 * `/members-page/{people,groups,service-accounts,requests,roles}`.
 */
export const MembersOverview = z.object({
  counts: z.object({
    people: z.number().int(),
    groups: z.number().int(),
    serviceAccounts: z.number().int(),
    requests: z.number().int(),
    roles: z.number().int(),
  }),
  pendingInvites: z.number().int(),
  scimSync: z.string(),
});
export type MembersOverview = z.infer<typeof MembersOverview>;

/** One server-side page of the People tab. */
export const MembersPeoplePage = z.object({
  members: z.array(WorkspaceMember),
  total: z.number().int(),
});
export type MembersPeoplePage = z.infer<typeof MembersPeoplePage>;

export const MembersAction = z.object({
  action: z.enum([
    "set_role",
    "deactivate",
    "reactivate",
    "grant",
    "revoke_grant",
    "approve",
    "deny",
    "invite",
    "add_mapping",
    "remove_mapping",
    "toggle_sa",
    "sync",
  ]),
  ids: z.array(z.string()).optional(),
  role: z.string().optional(),
  project: z.string().optional(),
  requestId: z.string().optional(),
  emails: z.array(z.string()).optional(),
  groupId: z.string().optional(),
  saId: z.string().optional(),
});
export type MembersAction = z.infer<typeof MembersAction>;

export const WorkspaceChannel = Channel.extend({
  ownerTeam: z.string(),
  allowed: z.string(),
  rules: z.number().int(),
});
export type WorkspaceChannel = z.infer<typeof WorkspaceChannel>;

export const ChannelInput = z.object({
  kind: Channel.shape.kind,
  name: z.string().min(1),
  target: z.string(),
});
export type ChannelInput = z.infer<typeof ChannelInput>;

export const ChannelsAction = z.object({
  action: z.enum(["remove"]),
  id: z.string().optional(),
});
export type ChannelsAction = z.infer<typeof ChannelsAction>;

/**
 * Only what the database can answer today. Seats live here and nowhere else: Members reads the
 * same numbers rather than keeping a second copy.
 */
export const BillingPage = z.object({
  plan: z.string(),
  seats: z.object({ used: z.number().int(), total: z.number().int() }),
  projects: z.number().int(),
  runs30d: z.number().int(),
});
export type BillingPage = z.infer<typeof BillingPage>;

export const AuditEvent = z.object({
  id: z.string(),
  when: z.string(),
  actor: z.string(),
  initials: z.string().nullable(),
  tag: z.enum(["RUN", "REPO", "JOB", "ACCESS", "ALERT", "CONNECTION", "SETTINGS"]),
  text: z.string(),
  project: z.string(),
  source: z.string(),
  ageDays: z.number(),
});
export type AuditEvent = z.infer<typeof AuditEvent>;

export const AuditPage = z.object({
  events: z.array(AuditEvent),
  streams: z.array(
    z.object({ id: z.string(), name: z.string(), target: z.string(), enabled: z.boolean() }),
  ),
});
export type AuditPage = z.infer<typeof AuditPage>;

export const WorkspaceSettingsPage = z.object({
  general: z.object({
    name: z.string(),
    slug: z.string(),
    region: z.string(),
    dbtVersion: z.string(),
  }),
  sso: z.object({
    provider: z.string(),
    domains: z.string(),
    requireSso: z.boolean(),
    scim: z.boolean(),
    jit: z.boolean(),
  }),
  security: z.object({
    session: z.string(),
    allowlist: z.string(),
  }),
  tokens: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      prefix: z.string(),
      scope: z.string(),
      lastUsed: z.string(),
    }),
  ),
  projectsCount: z.number().int(),
});
export type WorkspaceSettingsPage = z.infer<typeof WorkspaceSettingsPage>;

export const WorkspaceSettingsAction = z.object({
  action: z.enum([
    "save_general",
    "set_sso",
    "set_security",
    "create_token",
    "revoke_token",
    "toggle_stream",
  ]),
  general: WorkspaceSettingsPage.shape.general.optional(),
  key: z.string().optional(),
  value: z.union([z.boolean(), z.string()]).optional(),
  tokenId: z.string().optional(),
  tokenName: z.string().optional(),
  tokenScope: z.string().optional(),
  tokenExpiryDays: z.number().int().optional(),
});
export type WorkspaceSettingsAction = z.infer<typeof WorkspaceSettingsAction>;

export const RepoCandidate = z.object({
  fullName: z.string(),
  meta: z.string(),
  branch: z.string(),
  subdirectory: z.string(),
  kind: z.enum(["ok", "none", "inuse"]),
  models: z.number().int(),
});
export type RepoCandidate = z.infer<typeof RepoCandidate>;

/** One server-side page of `GET /repositories?q=&limit=`. */
export const RepoCandidatePage = z.object({
  repos: z.array(RepoCandidate),
  total: z.number().int(),
});
export type RepoCandidatePage = z.infer<typeof RepoCandidatePage>;

export const NewProjectInput = z.object({
  name: z.string(),
  repo: z.string(),
  branch: z.string(),
  subdirectory: z.string(),
  ownerTeamId: z.string(),
  environments: z.array(
    z.object({
      name: z.string(),
      kind: z.enum(["production", "staging", "ci"]),
      connectionId: z.string(),
      schema: z.string(),
      threads: z.number().int(),
    }),
  ),
  job: z
    .object({
      name: z.string(),
      commands: z.array(z.string()),
      trigger: JobTrigger,
      cron: z.string().nullable(),
      channelIds: z.array(z.string()),
    })
    .nullable(),
  runNow: z.boolean(),
  slimCi: z.boolean(),
  docs: z.boolean(),
});
export type NewProjectInput = z.infer<typeof NewProjectInput>;

// ---------------------------------------------------------------------------
// Runner protocol (bearer RUNNER_TOKEN, /api/runner/*)
// ---------------------------------------------------------------------------

/**
 * Runner protocol versions the API serves. A runner states its version in `X-Runner-Protocol`;
 * an unsupported one is refused at claim (426), never mid-run. Remove a version only when no
 * running run carries it (`docs/PROD-RELEASES.md`).
 *   2 — per-run tokens after claim (2026-09-10). 1 (shared token throughout) is gone.
 */
export const RUNNER_PROTOCOLS = [2] as const;
export const RUNNER_PROTOCOL_CURRENT = 2;

export const RunnerClaimRequest = z.object({
  runnerId: z.string().min(1),
  adapters: z.array(z.string()).default([]),
});
export type RunnerClaimRequest = z.infer<typeof RunnerClaimRequest>;

export const RunSpec = z.object({
  runId: z.string(),
  /** Bearer for every call about this run after claim; dies with the run. Never logged. */
  runToken: z.string(),
  number: z.number().int(),
  workspace: z.string(),
  project: z.string(),
  repo: z.object({
    fullName: z.string(),
    branch: z.string(),
    sha: z.string().nullable(),
    subdirectory: z.string(),
  }),
  environment: z.object({
    slug: z.string(),
    schema: z.string(),
    threads: z.number().int(),
    connectionType: ConnectionType,
    /** Resolved dbt profile target; never persisted or logged. */
    profile: z.record(z.string(), z.unknown()),
  }),
  steps: z.array(
    z.object({ index: z.number().int(), name: z.string(), command: z.string().nullable() }),
  ),
  envVars: z.record(z.string(), z.string()),
  generateDocs: z.boolean(),
  defer: z.boolean(),
  /** Run-token path that serves the production manifest for slim CI (`--defer --state`); null when none exists yet. */
  deferManifest: z.string().nullable().default(null),
});
export type RunSpec = z.infer<typeof RunSpec>;

export const RunnerStepUpdate = z.object({
  state: StepState,
  sub: z.string().optional(),
  errors: z.number().int().optional(),
  warnings: z.number().int().optional(),
  seconds: z.number().optional(),
});
export type RunnerStepUpdate = z.infer<typeof RunnerStepUpdate>;

export const RunnerLogBatch = z.object({
  step: z.number().int(),
  lines: z.array(
    z.object({
      ts: z.string(),
      level: LogLevel,
      msg: z.string(),
      hl: z.boolean().optional(),
      routine: z.boolean().optional(),
    }),
  ),
});
export type RunnerLogBatch = z.infer<typeof RunnerLogBatch>;

export const RunnerResultsBatch = z.object({
  step: z.number().int(),
  nodes: z.array(
    z.object({
      uniqueId: z.string(),
      type: z.enum(["model", "test", "seed", "snapshot", "source", "operation"]),
      name: z.string(),
      status: z.string(),
      executionMs: z.number().int(),
      rows: z.number().int().nullable(),
      failures: z.number().int().nullable(),
      message: z.string(),
      /** Source nodes only: dbt's freshness age in minutes. Never reuse `rows` for this. */
      freshnessMinutes: z.number().int().nullable().optional(),
      /** Source nodes only: dbt's `max_loaded_at`, ISO. */
      maxLoadedAt: z.string().nullable().optional(),
    }),
  ),
});
export type RunnerResultsBatch = z.infer<typeof RunnerResultsBatch>;

export const RunnerFinish = z.object({
  status: z.enum(["succeeded", "warning", "failed", "cancelled"]),
  summary: z.string(),
});
export type RunnerFinish = z.infer<typeof RunnerFinish>;

/** Handed to a runner instead of a run: prove a connection works with `dbt debug`. */
export const ConnectionTestSpec = z.object({
  kind: z.literal("connection_test"),
  testId: z.string(),
  /** Bearer for the finish call; only valid for this test. */
  runToken: z.string(),
  connectionType: ConnectionType,
  profile: z.record(z.string(), z.unknown()),
});
export type ConnectionTestSpec = z.infer<typeof ConnectionTestSpec>;
export const ConnectionTestFinish = z.object({ ok: z.boolean(), message: z.string() });

export const RunnerHeartbeat = z.object({ cancelRequested: z.boolean(), status: RunStatus });
export type RunnerHeartbeat = z.infer<typeof RunnerHeartbeat>;
